/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useState, useEffect, useContext } from 'react';
import Alert from 'react-s-alert';
import { Tooltip, Modal, ModalBody, ModalHeader, ModalFooter, Button, FormGroup, Label, Col, Input, InputGroup, InputGroupAddon } from 'reactstrap';
import { Link, Prompt } from 'react-router-dom';
import Toggle from 'react-toggle';
import Joyride, { STATUS } from 'react-joyride';

// context imports
import { ThemeContext } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';

// service imports
import AuthService from '../../services/AuthService';
import ProviderService from '../../services/ProviderService';
import GeneralService from '../../services/GeneralService';
import ChatService from '../../services/ChatService';

// local imports
import UserNotApproved from './UserNotApproved';
import ErrorBoundary from '../general/ErrorBoundary';
import ProviderNavbar from './ProviderNavbar';
import Message from '../general/Message';

const MyPricing = props => {

    // states
    const [isLoading, setIsLoading] = useState(false);
    const [isAddLoading, setIsAddLoading] = useState(false);
    const [isUpdateLoading, setIsUpdateLoading] = useState(false);
    const [isRemoveLoading, setIsRemoveLoading] = useState(false);
    const [clientSubscriptionPlans, setClientSubscriptionPlans] = useState({
        isActive: false,
        plans: [],
        planRange: []
    });
    const [editPlanModal, setEditPlanModal] = useState({ isVisible: false, type: "update", data: { id: "", planId: "", name: "", amount: 0, call: 0, video: 0, report: false } });
    const [confirmationModal, setConfirmationModal] = useState({ isVisible: false, selectedIndex: -1 });
    const [pricing, setPricing] = useState([]);
    const [timeSlotSize, setTimeSlotSize] = useState(0);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [reportChargetooltipOpen, setReportChargetooltipOpen] = useState(false);
    const [isDataModified, setIsDataModified] = useState(false);
    const [profileComplete, setProfileComplete] = useState({
        status: true,
        message: {
            msgBody: [`Looks like your profile is incomplete! Please complete that by going to `, <b key="profileLink"><Link to='/counsellor/profile'>Profile</Link></b>, ` page to enjoy our service. Unless your profile is completed you won't be visible to any clients.`],
            msgError: true
        }
    });
    const [priceUpdate, setPriceUpdate] = useState({
        status: true,
        message: {
            msgBody: [`Base price for services have been changed. Please make note of it and update your pricing if needed.`],
            msgError: true
        }
    });
    const [tutorial, setTutorial] = useState({
        status: false,
        steps: [
            {
                content: <h2>Manage your pricing from here!</h2>,
                placement: 'center',
                target: '#myPricingDiv',
            },
            {
                content: <div className="tooltipDiv">Pricing should be at least the base charge.</div>,
                placement: 'right',
                target: '#basePriceForCall',
            },
            {
                content: <div className="tooltipDiv">Add or edit subscription plans for your clients from here.</div>,
                placement: 'right',
                target: '#subscriptionPlans',
            }
        ]
    });
    let helpers = null;
    const [notifications, setNotifications] = useState({
        incompleteProfile: [],
        minimumServiceChargesUpdated: [],
        minimumReportChargeUpdated: []
    });

    // contexts
    const { theme, colors } = useContext(ThemeContext);
    const { user, setUser, userApprovalStatus } = useContext(AuthContext);

    // utility functions
    const toggle = () => setTooltipOpen(!tooltipOpen);
    const toggleReportChargetooltip = () => setReportChargetooltipOpen(!reportChargetooltipOpen);

    // useEffect
    useEffect(() => {
        // if (!user.guideTakenFor.includes("MyPricing")) {
        //     setTutorial({ ...tutorial, status: true });
        // }
        ProviderService.getPricings()
            .then(data => {
                try {
                    if (!user.guideTakenFor.includes("MyPricing")) {
                        setTutorial({ ...tutorial, status: true });
                    }
                    setTimeSlotSize(data.timeSlotSize);
                    setPricing(data.pricing);
                } catch (err) {
                    Alert.error("Some error occured, please try again later or report support team.", {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    GeneralService.logError('at Provider MyPricing useEffect getPricing', 'Error in try catch block', err.stack);
                }
            })
        getClientSubscriptionPlans();
        getNotifications();
        return () => {
            window.onbeforeunload = undefined
            setIsDataModified(false);
        }
    }, []);

    useEffect(() => {
        if (isDataModified) {
            window.onbeforeunload = () => true
        } else {
            window.onbeforeunload = undefined
        }
    }, [isDataModified]);

    const getNotifications = () => {
        AuthService.getNotifications().then(data => {
            try {
                let temp = { minimumServiceChargesUpdated: [], minimumReportChargeUpdated: [] };
                Promise.all(data.notifications.MyPricing.map(item => {
                    if (!item.readStatus) {
                        if (item.title === "minimumServiceChargesUpdated" || item.title === "minimumReportChargeUpdated") {
                            setPriceUpdate({ ...priceUpdate, status: true });
                            temp[item.title].push(item.description);
                            AuthService.markNotificationAsRead("ProviderSession", item._id);
                            // temp.minimumServiceChargesUpdated.push(item.description);
                            // temp.minimumReportChargeUpdated.push(item.description);
                        } else if (item.title === "incompleteProfile") {
                            temp.incompleteProfile.push(item.description);
                            setProfileComplete({ ...profileComplete, status: false });
                        }
                    }
                })).then(() => {
                    setNotifications(temp);
                });
            } catch (err) {
                Alert.error("Some error occured, please try again later or report support team.", {
                    position: 'top-right',
                    effect: 'slide'
                });
                GeneralService.logError('at Provider MyPricing getNotifications', 'Error in try catch block', err.stack);
            }
        })
    }

    const getClientSubscriptionPlans = async () => {
        try {
            let { data } = await ProviderService.getClientSubscriptionPlans();
            // console.log("data", data);
            let tempArray = [];
            if (data.isActive) {
                for (let i = data.clientSubscriptionPlanRange.from; i <= data.clientSubscriptionPlanRange.to; i += data.clientSubscriptionPlanRange.interval) {
                    tempArray.push(i);
                }
            }
            setClientSubscriptionPlans({ isActive: data.isActive, plans: data.clientSubscriptionPlans, planRange: tempArray });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Provider MyPricing useEffect getClientSubscriptionPlans', 'Error in try catch block', err.stack);
        }
    }

    // handle functions
    const handleChange = (e, index) => {
        try {
            let temp = [...pricing];
            temp[index].currentPrice = Number.parseInt(e.target.value);
            setPricing(temp);
            setIsDataModified(true);
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Provider MyPricing handleChange', 'Error in try catch block', err.stack);
        }
    }

    const handleSubmit = e => {
        e.preventDefault();
        try {
            setIsLoading(true);
            ProviderService.setPricings(pricing.reduce((r, e) => { r[e.id] = e.currentPrice; return r; }, {}))
                .then(data => {
                    Alert.info(data.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsDataModified(false);
                    setIsLoading(false);
                })
                .catch(error => {
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading(false);
                });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading(false);
            GeneralService.logError('at Provider MyPricing handleSubmit', 'Error in try catch block', err.stack);
        }
    }

    const handlePlanDetailChange = e => {
        try {
            e.preventDefault();
            if (e.target.id === "amount") {
                setEditPlanModal({ ...editPlanModal, data: { ...editPlanModal.data, amount: Number(e.target.value) } });
            } else if (e.target.id === "report") {
                setEditPlanModal({ ...editPlanModal, data: { ...editPlanModal.data, [e.target.name]: e.target.checked } });
            } else {
                setEditPlanModal({ ...editPlanModal, data: { ...editPlanModal.data, [e.target.name]: e.target.value } });
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Provider MyPricing handlePlanDetailChange', 'Error in try catch block', err.stack);
        }
    }

    // new need to add inPerson in clientSubscription - to be added as regular session
    const handleAddPlan = () => {
        setIsAddLoading(true);
        const { /*planId,*/ name, call, video, report, amount } = editPlanModal.data;
        ProviderService.createClientSubscriptionPlan(/*planId, */name, call, video, report, amount).then(data => {
            getClientSubscriptionPlans();
            setEditPlanModal({ isVisible: false, type: "update", data: { id: "", planId: "", name: "", amount: 0, call: 0, video: 0, report: false } });
            setIsAddLoading(false);
        })
    }

    const handleUpdatePlan = () => {
        setIsUpdateLoading(true);
        const { id/*, planId*/, name, call, video, report, amount } = editPlanModal.data;
        ProviderService.updateClientSubscriptionPlan(id/*, planId*/, name, call, video, report, amount).then(data => {
            getClientSubscriptionPlans();
            setEditPlanModal({ isVisible: false, type: "update", data: { id: "", planId: "", name: "", amount: 0, call: 0, video: 0, report: false } });
            setIsUpdateLoading(false);
        })
    }

    const handleRemovePlan = (index) => {
        setIsRemoveLoading(true);
        ProviderService.deleteClientSubscriptionPlan(clientSubscriptionPlans.plans[index].id).then(data => {
            getClientSubscriptionPlans();
            setConfirmationModal({ isVisible: false, selectedIndex: -1 });
            setIsRemoveLoading(false);
        })
    }

    const handleJoyrideCallback = (data) => {
        const { status, type } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status)) {
            if (!user.guideTakenFor.includes("MyPricing")) {
                AuthService.markVisited("MyPricing");
                setUser({ ...user, guideTakenFor: [...user.guideTakenFor, "MyPricing"] });
            }
            setTutorial({ ...tutorial, status: false });
        }
    };

    const getHelpers = (paramHelpers) => {
        helpers = paramHelpers;
    };

    return (
        <div id="myPricingDiv">
            <Prompt
                when={isDataModified}
                message='You have unsaved changes, are you sure you want to leave?'
            />
            <ErrorBoundary errorAt="At Provider MyPricing ProviderNavbar">
                <ProviderNavbar history={props.history} showTutorial={() => setTutorial({ ...tutorial, status: true })} />
            </ErrorBoundary>
            <ErrorBoundary errorAt="At Provider MyPricing Joyride">
                <Joyride
                    callback={handleJoyrideCallback}
                    continuous={true}
                    getHelpers={getHelpers}
                    run={false && tutorial.status}
                    scrollToFirstStep={true}
                    showProgress={true}
                    showSkipButton={true}
                    steps={tutorial.steps}
                    locale={{ last: 'Finish' }}
                    styles={{
                        options: {
                            primaryColor: "rgb(56, 78, 115)",
                            zIndex: 10000,
                        },
                    }}
                />
            </ErrorBoundary>
            {
                userApprovalStatus ?
                    <div>
                        <ErrorBoundary errorAt="At Provider MyPricing ProfileCompleteMessage">
                            {profileComplete.status ? null : <Message message={profileComplete.message} />}
                        </ErrorBoundary>
                        <div className="p-3">

                            <form onSubmit={handleSubmit}>
                                <div>
                                    <h1 id="my-pricing-heading" style={theme.headingBold}>My Pricing</h1>
                                    <hr style={{ borderWidth: "2px" }} />
                                </div>

                                <ErrorBoundary errorAt="At Provider MyPricing PriceUpdateMessage">
                                    {priceUpdate.status ? null : <Message message={priceUpdate.message} />}
                                </ErrorBoundary>

                                <ErrorBoundary errorAt="At Provider MyPricing Prices">
                                    <div className="text-center d-flex" style={{ overflowX: "scroll", whiteSpace: "nowrap" }}>
                                        {
                                            pricing.map((item, index) => (
                                                <div key={index} className="d-inline-block col-8 col-sm-8 col-md-3 text-center p-2 m-3" style={theme.card}>
                                                    {
                                                        item.id === "reportCharge" ?
                                                            <div style={{ cursor: "pointer", position: "absolute", right: "0.5rem", top: "0.5rem" }} >
                                                                <i id="reportChargeInfo" className="fas fa-info-circle"></i>
                                                                <Tooltip style={{ textAlign: "left" }} placement="right" isOpen={reportChargetooltipOpen} target="reportChargeInfo" toggle={toggleReportChargetooltip}>
                                                                    Provide your client with a Report from your side.<br />Report must be generated by you outside the Umang Wellness Platform and mailed to the client.
                                                                </Tooltip>
                                                            </div>
                                                            : null
                                                    }
                                                    <h2 style={theme.subHeadingSemibold}>{item.name}</h2>
                                                    <br />
                                                    <div className="row px-3 pb-3">
                                                        <label htmlFor={item.id} className="col-md-6 my-auto">Rate ₹</label>
                                                        <input type="number" step={50} min={item.basePrice} id={item.id} name={item.id} size="5" maxLength="5" className="text-center form-control col-md-5" value={item.currentPrice} onChange={e => handleChange(e, index)} style={theme.formInput} required></input>
                                                    </div>
                                                    <p style={theme.paragraph} id={`basePriceFor${item.name}`}>{`Base - ₹${item.basePrice} `}{item.id !== "reportCharge" ? `for ${timeSlotSize} minutes` : `per Client`}</p>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </ErrorBoundary>

                                <div className="d-flex">
                                    <button type="submit" className="btn mr-3 my-auto" style={isDataModified ? theme.buttonWarning : theme.button} disabled={isLoading}>{isLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Save</button>
                                    <div>
                                        <p className="my-2">*Rate should be at least base charge</p>
                                    </div>
                                </div>
                            </form>
                        </div>

                        <div className="p-3">
                            <div>
                                <h1 id="my-pricing-subscription-plans" style={theme.headingBold}>Subscription plans</h1>
                                <hr style={{ borderWidth: "2px" }} />
                            </div>

                            <ErrorBoundary errorAt="At Provider MyPricing ClientSubscriptionPlans">
                                <div id="subscriptionPlans">
                                    {
                                        clientSubscriptionPlans.isActive ?
                                            <div className="text-center d-flex" style={{ overflowX: "scroll", overflowY: "hidden", whiteSpace: "nowrap" }}>
                                                {
                                                    clientSubscriptionPlans.plans.map((item, index) => (
                                                        <div key={index} className="d-inline-block col-8 col-sm-8 col-md-3 text-center px-2 py-3 m-3" style={theme.card}>
                                                            <div style={{ cursor: "pointer", position: "absolute", right: "0.5rem", top: "0.5rem" }} onClick={() => setEditPlanModal({ isVisible: true, type: "update", data: item })}><i className="fas fa-edit"></i></div>
                                                            <h2 style={Object.assign({ color: "#384E73" }, theme.subHeadingSemibold)}>{item.name}</h2>
                                                            <p>{`₹${item.amount / 100} / ${item.period}`}</p>
                                                            <div>
                                                                <p><i className={`fas fa-${item.call > 0 ? 'check' : 'times danger_text_color'} mr-2`}></i>{`${item.call} Call Sessions / ${item.period}`}</p>
                                                                <p><i className={`fas fa-${item.video > 0 ? 'check' : 'times danger_text_color'} mr-2`}></i>{`${item.video} Video Sessions / ${item.period}`}</p>
                                                                <p><i className={`fas fa-${item.report ? 'check' : 'times danger_text_color'} mr-2`}></i>{`Report Included`}</p>
                                                                <p><i className="fas fa-check mr-2"></i>{`Unlimited Messages`}</p>
                                                            </div>
                                                            <div style={{ color: colors.danger, cursor: "pointer" }} onClick={() => setConfirmationModal({ isVisible: true, selectedIndex: index })}>
                                                                Remove
                                                            </div>
                                                        </div>
                                                    ))
                                                }
                                                {
                                                    clientSubscriptionPlans.plans.length < 3 ?
                                                        <div id="add-plans" className="d-flex col-8 col-sm-8 col-md-3 text-center px-2 py-3 m-3" style={Object.assign({ cursor: "pointer" }, theme.card)} onClick={() => setEditPlanModal({ isVisible: true, type: "add", data: { id: "", planId: "", name: "", amount: 0, call: 0, video: 0, report: false, period: "Month" } })}>
                                                            <div className="m-auto">
                                                                <div className="d-flex">
                                                                    <div>
                                                                        <h2 style={{ color: "#384E73", fontFamily: "Montserrat, sans-serif", fontSize: "3rem", fontWeight: "600" }}>{`+`}</h2>
                                                                    </div>
                                                                    <div>
                                                                        <h2 style={Object.assign({ color: "#384E73" }, theme.subHeadingSemibold)}>{`Add plan`}</h2>
                                                                        <p>{`${3 - clientSubscriptionPlans.plans.length} left`}</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        : null
                                                }
                                            </div>
                                            :
                                            <p>Client subscription plans feature is not available under current subscriptions.</p>
                                    }
                                </div>
                            </ErrorBoundary>
                        </div>
                        <ErrorBoundary errorAt="At Client MyPricing ConfirmationModal">
                            <Modal size="sm" isOpen={confirmationModal.isVisible} backdrop={true}>
                                <ModalHeader close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => setConfirmationModal({ isVisible: false, selectedIndex: -1 })}><i className="fas fa-times"></i></span>}><span style={theme.headingBold}>Are you sure?</span></ModalHeader>
                                <ModalBody>
                                    {
                                        confirmationModal.selectedIndex >= 0 ?
                                            <Label>By clicking on Remove button <b>{clientSubscriptionPlans.plans[confirmationModal.selectedIndex].name}</b> will be <b>Deleted</b>.</Label>
                                            : null
                                    }
                                </ModalBody>
                                <ModalFooter>
                                    <Button type="button" style={theme.formButton} className="btn btn-danger" onClick={() => handleRemovePlan(confirmationModal.selectedIndex)}>
                                        {isRemoveLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Remove
                                    </Button>
                                </ModalFooter>
                            </Modal>
                        </ErrorBoundary>
                        <Modal size="lg" isOpen={editPlanModal.isVisible} backdrop={true}>
                            <ModalHeader id="my-pricing-edit-plans" close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => setEditPlanModal({ isVisible: false, type: "update", data: { id: "", name: "", amount: 0, call: 0, video: 0, report: false } })}><i className="fas fa-times"></i></span>}><span style={theme.headingBold}>Edit plan</span></ModalHeader>
                            <ModalBody>
                                <div className="text-center">
                                    <div className="d-inline-block col-8 col-sm-8 col-md-4 text-center px-2 py-3 m-3" style={theme.card}>
                                        <h2 style={Object.assign({ color: "#384E73" }, theme.subHeadingSemibold)}>{editPlanModal.data.name}</h2>
                                        <p>{`₹${editPlanModal.data.amount / 100} / ${editPlanModal.data.period}`}</p>
                                        <div>
                                            <p><i className={`fas fa-${editPlanModal.data.call > 0 ? 'check' : 'times danger_text_color'} mr-2`}></i>{`${editPlanModal.data.call} Call Sessions / ${editPlanModal.data.period}`}</p>
                                            <p><i className={`fas fa-${editPlanModal.data.video > 0 ? 'check' : 'times danger_text_color'} mr-2`}></i>{`${editPlanModal.data.video} Video Sessions / ${editPlanModal.data.period}`}</p>
                                            <p><i className={`fas fa-${editPlanModal.data.report ? 'check' : 'times danger_text_color'} mr-2`}></i>{`Report Included`}</p>
                                            <p><i className="fas fa-check mr-2"></i>{`Unlimited Messages`}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-3 m-3" style={theme.card}>
                                    <FormGroup row>
                                        <Label sm={4} for="name">Plan name</Label>
                                        <Col sm={8}>
                                            <Input type="text" name="name" id="name" value={editPlanModal.data.name} onChange={handlePlanDetailChange} required />
                                        </Col>
                                    </FormGroup>
                                    {/* <FormGroup row>
                            <Label sm={4} for="name">Plan tier</Label>
                            <Col sm={8}>
                                <Input type="text" value={editPlanModal.data.tierLevel} disabled={true} />
                            </Col>
                        </FormGroup> */}
                                    <FormGroup row>
                                        <Label sm={4} for="amount">Rate</Label>
                                        <Col sm={8}>
                                            <InputGroup>
                                                <InputGroupAddon addonType="prepend">₹</InputGroupAddon>
                                                <Input type="select" name="amount" id="amount" value={editPlanModal.data.amount} onChange={handlePlanDetailChange} required>
                                                    <option value="">Select amount</option>
                                                    {
                                                        clientSubscriptionPlans.planRange.map((item, index) => (
                                                            <option key={index} value={item * 100}>{item}</option>
                                                        ))
                                                    }
                                                </Input>
                                            </InputGroup>
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row>
                                        <Label sm={4} for="call">Call</Label>
                                        <Col sm={8}>
                                            <InputGroup>
                                                <Input type="number" name="call" id="call" min={0} value={editPlanModal.data.call} onChange={handlePlanDetailChange} required />
                                                <InputGroupAddon addonType="append">sessions</InputGroupAddon>
                                            </InputGroup>
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row>
                                        <Label sm={4} for="video">Video</Label>
                                        <Col sm={8}>
                                            <InputGroup>
                                                <Input type="number" name="video" id="video" min={0} value={editPlanModal.data.video} onChange={handlePlanDetailChange} required />
                                                <InputGroupAddon addonType="append">sessions</InputGroupAddon>
                                            </InputGroup>
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row>
                                        <Label sm={4} for="report">Report</Label>
                                        <Col sm={8}>
                                            <Toggle
                                                id="report"
                                                name="report"
                                                icons={false}
                                                checked={editPlanModal.data.report}
                                                onChange={handlePlanDetailChange}
                                            />
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row>
                                        <Label sm={4} for="chat">Unlimited chat Sessions</Label>
                                        <Col sm={8}>
                                            <p><i className="fas fa-check mr-2"></i></p>
                                        </Col>
                                    </FormGroup>
                                </div>
                            </ModalBody>
                            <ModalFooter className="justify-content-start">
                                {
                                    editPlanModal.type === "update" ?
                                        <Button type="button" style={theme.formButton} className="btn" onClick={() => handleUpdatePlan()}>
                                            {isUpdateLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}{"Update plan"}
                                        </Button>
                                        : <Button type="button" style={theme.formButton} className="btn" onClick={() => handleAddPlan()}>
                                            {isAddLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}{"Add plan"}
                                        </Button>
                                }
                            </ModalFooter>
                        </Modal>
                    </div>
                    :
                    <UserNotApproved />
            }
        </div>
    )

}

export default MyPricing;